import format from 'date-fns/format'

/**
 * Parses a date and returns its parts (day, month, year) as zero-padded strings.
 * @param {Date|string|number} date - The date to parse (accepts Date object, date string, or timestamp)
 * @returns {{day: string, month: string, year: number}|null} Object containing day, month, and year, or null if date is invalid
 */
const getFormattedDateParts = (date) => {
  const d = new Date(date)

  if (Number.isNaN(d.getTime())) {
    return null
  }

  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const year = d.getFullYear()

  return { day, month, year }
}

/**
 * Formats a date in MM-DD-YYYY format.
 * @param {Date|string|number} date - The date to format (accepts Date object, date string, or timestamp)
 * @returns {string} Formatted date string in MM-DD-YYYY format, or empty string if date is invalid
 */
export const formatDateMDY = (date) => {
  const formattedDateParts = getFormattedDateParts(date)
  if (!formattedDateParts) return ''

  const { month, day, year } = formattedDateParts
  return [month, day, year].join('-')
}

/**
 * Formats a date in DD-MM-YYYY format.
 * @param {Date|string|number} date - The date to format (accepts Date object, date string, or timestamp)
 * @returns {string} Formatted date string in DD-MM-YYYY format, or empty string if date is invalid
 */
export const formatDateDMY = (date) => {
  const formattedDateParts = getFormattedDateParts(date)
  if (!formattedDateParts) return ''

  const { day, month, year } = formattedDateParts
  return [day, month, year].join('-')
}

/**
 * Formats a date in YYYY-MM-DD format.
 * @param {Date|string|number} date - The date to format (accepts Date object, date string, or timestamp)
 * @returns {string} Formatted date string in YYYY-MM-DD format, or empty string if date is invalid
 */
export const formatDateYMD = (date) => {
  const formattedDateParts = getFormattedDateParts(date)
  if (!formattedDateParts) return ''

  const { year, month, day } = formattedDateParts
  return [year, month, day].join('-')
}

export const getDateTime = (dateTime) => {
  const d = new Date(dateTime)
  let month = '' + (d.getMonth() + 1)
  let day = '' + d.getDate()
  const year = d.getFullYear()
  let hours = d.getHours()
  let minutes = d.getMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12
  hours = hours || 12 // the hour '0' should be '12'
  minutes = minutes < 10 ? '0' + minutes : minutes
  const time = hours + ':' + minutes + ' ' + ampm

  if (month.length < 2) month = '0' + month
  if (day.length < 2) day = '0' + day

  const formatedDateTime = `${month}/${day}/${year} - ${time}`

  return formatedDateTime
}

export const getDateDaysAgo = (days) => {
  const now = new Date()
  now.setDate(now.getDate() - days)
  return now
}

/**
 * Returns a local-timezone-based datetime string for a given date with the
 * correct numeric timezone offset (e.g. +05:30, -04:00).
 * Uses date-fns for formatting. When type is 'start', time is set to 00:00:00.000 local;
 * when 'end', time is set to 23:59:59.999 local. For any other value, preserves time.
 * Accepts a Date, string, or single-element array (to be compatible with existing calls).
 * @param {Date|string|Array} date
 * @param {'start'|'end'|undefined} type
 * @returns {string} e.g. '2025-11-06T00:00:00+05:30'
 */
export const formatApiDateTimeWithOffset = (date, type = 'start') => {
  const input = Array.isArray(date) ? date[0] : date
  const localDate = new Date(input)

  if (type === 'start') {
    localDate.setHours(0, 0, 0, 0)
  } else if (type === 'end') {
    localDate.setHours(23, 59, 59, 999)
  }

  const tzOffsetMinutes = -localDate.getTimezoneOffset() // positive for zones east of UTC
  const sign = tzOffsetMinutes >= 0 ? '+' : '-'
  const abs = Math.abs(tzOffsetMinutes)
  const hh = String(Math.floor(abs / 60)).padStart(2, '0')
  const mm = String(abs % 60).padStart(2, '0')
  const dateTime = format(localDate, "yyyy-MM-dd'T'HH:mm:ss")
  return `${dateTime}${sign}${hh}:${mm}`
}

/**
 * Formats a date in the format: "24th May 2026 12:23PM"
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDateWithOrdinal = (date) => {
  const d = new Date(date)
  // Use date-fns format: 'do' for ordinal day, 'MMMM' for full month, 'yyyy' for year, 'h:mmA' for 12-hour time with uppercase AM/PM
  try {
    return format(d, 'do MMMM yyyy h:mm aa')
  } catch (error) {
    console.error('formatDateWithOrdinal', error)
    return date
  }
}

/**
 * Formats a date as local date + time (12-hour AM/PM), e.g. "26-05-2026 05:29 AM".
 * Used to show bonus expiry in the user's local timezone with exact time.
 * @param {Date|string|number} date
 * @returns {string}
 */
export const formatExpiryLocalWithTime = (date) => {
  if (!date) return ''
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return ''

  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()

  let hours = d.getHours()
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12 || 12
  const hh = String(hours).padStart(2, '0')

  return `${day}-${month}-${year} ${hh}:${minutes} ${ampm}`
}

/**
 * Formats a date as UTC date + time, e.g. "25-05-2026 23:59 UTC".
 * Used in the ⓘ tooltip to show the authoritative UTC expiry.
 * @param {Date|string|number} date
 * @returns {string}
 */
export const formatExpiryUTC = (date) => {
  if (!date) return ''
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return ''

  const day = String(d.getUTCDate()).padStart(2, '0')
  const month = String(d.getUTCMonth() + 1).padStart(2, '0')
  const year = d.getUTCFullYear()

  let hours = d.getUTCHours()
  const minutes = String(d.getUTCMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12 || 12
  const hh = String(hours).padStart(2, '0')

  return `${day}-${month}-${year} ${hh}:${minutes} ${ampm} UTC`
}
